import React, { useState } from "react";

import useTranslation from "next-translate/useTranslation";

import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import Input, {
  WrapperInputs,
} from "@CheckoutOnepageOffers/components/atoms/Input";
import InputMask from "@CheckoutOnepageOffers/components/atoms/InputMask";
import { useCheckoutOnepageOffersContext } from "@CheckoutOnepageOffers/provider/useCheckoutOnepageOffersContext";
import { useAddress } from "@payment/hooks/useAddress";
import { Scope } from "@unform/core";

import { AddressFormContainer, Title } from "./styles";

interface IAddressForm {
  title: string;
}

const AddressForm: React.FC<IAddressForm> = ({ title }: IAddressForm) => {
  const { t } = useTranslation();
  const { formRef, checkoutOnePageState, checkoutOnePageDispatch } =
    useCheckoutOnepageOffersContext();
  const { getAddressBrazilAPIRequest } = useAddress();

  const [addressError, setAddressError] = useState<boolean>(false);

  const getAddressBrazilAPI = async (
    e: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const zipcode = e.currentTarget.value.replace(/[^0-9]/g, "");
    const MIN_ZIPCODE_LENGTH = 8;

    if (zipcode.length < MIN_ZIPCODE_LENGTH)
      return formRef.current.setFieldError("zipcode", "Digite um CEP válido");

    checkoutOnePageDispatch({
      type: "SET_CHECKOUT_ONE_PAGE_SETTINGS",
      payload: { loader: true },
    });

    const renderInputTimeout = setTimeout(() => setAddressError(true), 500);

    const address = await getAddressBrazilAPIRequest(zipcode);
    const { setFieldValue, setFieldError } = formRef.current;

    if (!address.error) {
      clearTimeout(renderInputTimeout);
      setAddressError(false);
      openNotificationWithIcon("success", t("common:zipCodeFoundWithSuccess"));

      setTimeout(() => {
        setFieldValue("address.city", address.city);
        setFieldValue("address.state", address.state);
        setFieldValue("address.street", address.street);
        setFieldValue("address.neighborhood", address.neighborhood);
      }, 200);
    } else if (address.error === "CEP não encontrado na base do ViaCEP") {
      setAddressError(true);
      openNotificationWithIcon("error", t("common:zipCodeNotFound"));
    } else {
      setAddressError(true);
      openNotificationWithIcon(
        "warning",
        t("common:addressHasIncompleteInformation"),
      );

      const requiredField = ["city", "address", "street", "neighborhood"];

      setTimeout(() => {
        setFieldError(`address.street_number`, "Campo obrigatório");

        Object.keys(address).forEach((key) => {
          if (requiredField.includes(key)) {
            if (!address[key])
              return setFieldError(`address.${key}`, "Campo obrigatório");

            setFieldValue(`address.${key}`, address[key]);
          }
        });
      }, 100);
    }

    checkoutOnePageDispatch({
      type: "SET_CHECKOUT_ONE_PAGE_SETTINGS",
      payload: { loader: false },
    });
  };

  return (
    <AddressFormContainer className="address-form-container">
      <Title>{title}</Title>

      <Scope path="address">
        <WrapperInputs>
          <InputMask
            required
            label="CEP"
            name="zipcode"
            mask="99999-999"
            autoComplete="false"
            isLoading={checkoutOnePageState?.loader}
            placeholder="00000-000"
            onBlur={getAddressBrazilAPI}
          />
          <Input
            required
            name="street_number"
            type="number"
            label="Número de residência"
            autoComplete="do-not-autofill"
            placeholder="Digite o número"
            inputMode="numeric"
          />
        </WrapperInputs>

        <WrapperInputs hidden={!addressError}>
          <Input
            required
            label="Rua"
            name="street"
            autoComplete="do-not-autofill"
            placeholder="Digite o nome da Rua"
          />
          <Input
            required
            label="Bairro"
            name="neighborhood"
            autoComplete="do-not-autofill"
            placeholder="Digite o nome do Bairro"
          />
        </WrapperInputs>

        <WrapperInputs className="address-wrapper" hidden={!addressError}>
          <Input
            required
            name="city"
            label="Cidade"
            autoComplete="do-not-autofill"
            placeholder="Digite o nome da Cidade"
          />
          <Input
            required
            name="state"
            label="Estado"
            autoComplete="do-not-autofill"
            placeholder="Digite o nome do Estado"
          />
        </WrapperInputs>
      </Scope>
    </AddressFormContainer>
  );
};

export default AddressForm;
